import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { addNotification } from '../../store/slices/uiSlice';
import { useDispatch } from 'react-redux';
import {
  WORKFLOW_REACTION_CATEGORIES,
  getReactionDisplay,
} from '../../utils/reactionConfig';
import { workflowItemLabel } from '../../utils/workflowTaskUtils';

const WORK_ITEM_TYPES = WORKFLOW_REACTION_CATEGORIES.find((c) => c.id === 'work-item-type')?.reactions || [];
const WORKFLOW_STATUSES = WORKFLOW_REACTION_CATEGORIES.find((c) => c.id === 'workflow-status')?.reactions || [];

const filterSelectClass =
  'w-full appearance-none [-webkit-appearance:none] [appearance:none] text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-2 pl-3 pr-8 focus:outline-none focus:ring-2 focus:ring-primary-500';

const WorkflowSearchDialog = ({
  isOpen,
  onClose,
  onWorkflowSelected,
  excludeMessageIds = [],
  mapping = false,
}) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [trackId, setTrackId] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const excluded = new Set((excludeMessageIds || []).map((id) => String(id)));

  const searchWorkflows = useCallback(async ({
    q,
    tagValue,
    typeValue,
    statusValue,
    trackValue,
    closed,
  }) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (tagValue) params.append('tag', tagValue);
    if (typeValue) params.append('type', typeValue);
    if (statusValue) params.append('status', statusValue);
    if (trackValue) params.append('trackId', trackValue);
    if (closed) params.append('includeClosed', 'true');
    const response = await axios.get(`${API_URL}/messages/workflows/search?${params.toString()}`, {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    });
    return Array.isArray(response.data) ? response.data : [];
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await searchWorkflows({
          q: query.trim(),
          tagValue: tag.trim(),
          typeValue: type,
          statusValue: status,
          trackValue: trackId.trim(),
          closed: includeClosed,
        });
        if (!cancelled) setResults(items);
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          dispatch(addNotification({
            type: 'error',
            message: typeof err?.response?.data === 'string' ? err.response.data : 'Failed to search workflows',
          }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query || tag || trackId ? 280 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, query, tag, type, status, trackId, includeClosed, dispatch, searchWorkflows]);

  const handleCancel = () => {
    setSelectedId(null);
    onClose?.();
  };

  const handleSelect = async () => {
    const selected = results.find((item) => String(item.messageId) === String(selectedId));
    if (!selected) {
      dispatch(addNotification({
        type: 'error',
        message: 'Please select a workflow to map.',
      }));
      return;
    }
    if (excluded.has(String(selected.messageId))) {
      dispatch(addNotification({
        type: 'error',
        message: 'This workflow is already mapped to the task.',
      }));
      return;
    }
    await onWorkflowSelected?.(selected);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1300] overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleCancel}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full transform transition-all">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 text-primary-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Map workflow
              </h3>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search workflows
              </label>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Search by title, content, track ID, tags, or sender…"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Track ID
                </label>
                <input
                  type="text"
                  value={trackId}
                  onChange={(e) => setTrackId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Filter by track ID"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Tag
                </label>
                <input
                  type="text"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Filter by tag"
                />
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Type
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className={filterSelectClass}
                >
                  <option value="">All types</option>
                  {WORK_ITEM_TYPES.map((item) => (
                    <option key={item.id} value={item.id}>{item.emoji} {item.label}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={filterSelectClass}
                >
                  <option value="">All statuses</option>
                  {WORKFLOW_STATUSES.map((item) => (
                    <option key={item.id} value={item.id}>{item.emoji} {item.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={includeClosed}
                onChange={(e) => setIncludeClosed(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              Include closed workflows
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select a workflow to map
              </label>
              {loading && results.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Searching workflows…</p>
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {query || tag || type || status || trackId
                    ? 'No workflows found matching your filters.'
                    : 'No workflows available.'}
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-200 dark:divide-gray-600">
                  {results.map((item) => {
                    const id = String(item.messageId || '');
                    const alreadyMapped = excluded.has(id);
                    const isSelected = String(selectedId) === id;
                    const title = (item.workflowTitle || '').trim() || workflowItemLabel(item);
                    const track = (item.workflowTrackId || '').trim();
                    const rowTitle = [track, title].filter(Boolean).join(' · ') || 'Workflow';
                    const typeId = item.workflowByCategory?.['work-item-type'];
                    const statusId = item.workflowByCategory?.['workflow-status'];
                    const typeDisplay = typeId ? getReactionDisplay(typeId) : null;
                    const statusDisplay = statusId ? getReactionDisplay(statusId) : null;
                    const tags = Array.isArray(item.customTags) ? item.customTags : [];
                    return (
                      <button
                        key={id}
                        type="button"
                        disabled={alreadyMapped}
                        onClick={() => !alreadyMapped && setSelectedId(id)}
                        className={`w-full text-left ${
                          alreadyMapped
                            ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-900/40'
                            : isSelected
                              ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-l-primary-600'
                              : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                        } transition-colors`}
                      >
                        <div className="flex items-center gap-3 p-4">
                          <div className="flex-shrink-0">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              isSelected
                                ? 'border-primary-600 bg-primary-600'
                                : 'border-gray-300 dark:border-gray-600'
                            }`}>
                              {isSelected ? (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4
                              className="text-sm font-semibold text-gray-900 dark:text-white truncate"
                              title={rowTitle}
                            >
                              {rowTitle}
                            </h4>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              {typeDisplay?.label ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                  {typeDisplay.emoji} {typeDisplay.label}
                                </span>
                              ) : null}
                              {statusDisplay?.label ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                  {statusDisplay.emoji} {statusDisplay.label}
                                </span>
                              ) : null}
                              {item.sourceLabel ? (
                                <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                                  {item.sourceLabel}
                                </span>
                              ) : null}
                              {alreadyMapped ? (
                                <span className="text-[10px] font-semibold text-primary-600 dark:text-primary-400">
                                  Already mapped
                                </span>
                              ) : null}
                              {tags.slice(0, 3).map((customTag) => (
                                <span
                                  key={customTag.id || customTag.label}
                                  className="text-[10px] px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200"
                                >
                                  {customTag.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSelect}
              disabled={!selectedId || mapping}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-primary-600 hover:bg-primary-500 disabled:opacity-50 border border-primary-600"
            >
              {mapping ? 'Mapping…' : 'Map workflow'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WorkflowSearchDialog;
